import React, { useEffect, useState } from 'react';
import {
  Bell,
  Plus,
  Trash2,
  Play,
  Pause,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Flame,
  X,
  Loader2,
  History,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation, formatCount } from '@/i18n';
import { useAlertStore } from '@/stores/alertStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import type { AlertRuleResponse, AlertEventResponse } from '@/utils/apiClient';
import { formatEventMessage, storedNumber } from '@/utils/alertEventMessage';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Option lists carry an i18n key (labelKey) resolved against t.alerts at render
// time, so the operator/channel/interval labels localize (STORY-155 EN-text fix).
const CONDITIONS = [
  { value: 'gt', labelKey: 'condGt' },
  { value: 'lt', labelKey: 'condLt' },
  { value: 'gte', labelKey: 'condGte' },
  { value: 'lte', labelKey: 'condLte' },
  { value: 'eq', labelKey: 'condEq' },
  { value: 'neq', labelKey: 'condNeq' },
];

const SEVERITIES = [
  { value: 'critical', labelKey: 'critical', color: 'text-red-600 dark:text-red-400' },
  { value: 'warning', labelKey: 'warning', color: 'text-yellow-600 dark:text-yellow-400' },
  { value: 'info', labelKey: 'info', color: 'text-blue-600 dark:text-blue-400' },
];

const CHANNELS = [
  { value: 'in_app', labelKey: 'chInApp' },
  { value: 'telegram', labelKey: 'chTelegram' },
  { value: 'email', labelKey: 'chEmail' },
  { value: 'webhook', labelKey: 'chWebhook' },
];

const INTERVALS = [
  { value: 30, labelKey: 'int30s' },
  { value: 60, labelKey: 'int1m' },
  { value: 300, labelKey: 'int5m' },
  { value: 600, labelKey: 'int10m' },
  { value: 1800, labelKey: 'int30m' },
  { value: 3600, labelKey: 'int1h' },
];

const RULE_KINDS = [
  { value: 'metric', labelKey: 'kindMetric' },
  { value: 'slo', labelKey: 'kindSlo' },
] as const;

// Resolve an alerts i18n key with a safe fallback to the key itself.
const alabel = (t: any, key: string): string => (t?.alerts?.[key] ?? key);

// Stored reasons from alert_evaluator. Never render the English string.
const SKIP_SLO_RECIPE = 'SLO recipe failed';
const SKIP_SLO_LIST = 'SLO answer was not a list';

function skipBannerKey(rule: { rule_kind?: string | null; last_skip_reason?: string | null }): string {
  if (rule.rule_kind !== 'slo') return 'ambiguousResponse';
  if (rule.last_skip_reason === SKIP_SLO_RECIPE) return 'skipSloRecipe';
  if (rule.last_skip_reason === SKIP_SLO_LIST) return 'skipSloList';
  return 'skipSlo';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function severityBadge(t: any, severity: string) {
  const s = SEVERITIES.find((sv) => sv.value === severity);
  const colorMap: Record<string, string> = {
    critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  };
  return (
    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', colorMap[severity] || 'bg-gray-100 text-gray-700')}>
      {s ? alabel(t, s.labelKey) : severity}
    </span>
  );
}

function statusBadge(t: any, status: string) {
  if (status === 'firing') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
        <Flame className="h-3 w-3" /> {alabel(t, 'firing')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
      <CheckCircle className="h-3 w-3" /> {alabel(t, 'resolved')}
    </span>
  );
}

function formatDateTime(iso: string | null, language?: string): string {
  if (!iso) return '\u2014';
  try {
    return new Date(iso).toLocaleString(language === 'en' ? 'en-US' : 'ru-RU');
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Form Modal
// ---------------------------------------------------------------------------

interface RuleFormData {
  name: string;
  rule_kind: 'metric' | 'slo';
  metric_query: string;
  condition: string;
  threshold: string;
  severity: string;
  channels: string[];
  webhook_url: string;
  evaluation_interval: number;
  description: string;
  entity_selector: string;
}

const emptyForm: RuleFormData = {
  name: '',
  rule_kind: 'metric',
  metric_query: '',
  condition: 'gt',
  threshold: '',
  severity: 'warning',
  channels: ['in_app'],
  webhook_url: '',
  evaluation_interval: 60,
  description: '',
  entity_selector: '',
};

const fieldClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm';

/** Body for POST/PUT. An SLO rule has no query, condition, or threshold;
 *  ``metric_query`` is the optional service name ("" = every violated SLO). */
export function ruleFormPayload(form: RuleFormData) {
  const notification_channels = {
    channels: form.channels,
    ...(form.webhook_url ? { webhook_url: form.webhook_url } : {}),
  };
  const base = {
    name: form.name,
    rule_kind: form.rule_kind,
    severity: form.severity,
    notification_channels,
    evaluation_interval: form.evaluation_interval,
    description: form.description || undefined,
  };
  if (form.rule_kind === 'slo') {
    return { ...base, metric_query: form.metric_query.trim() };
  }
  return {
    ...base,
    metric_query: form.metric_query,
    condition: form.condition,
    threshold: parseFloat(form.threshold),
    entity_selector: form.entity_selector || undefined,
  };
}

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RuleFormData) => Promise<void>;
  initial?: RuleFormData;
  title: string;
}

const FormModal: React.FC<FormModalProps> = ({ isOpen, onClose, onSubmit, initial, title }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<RuleFormData>(initial || emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setForm(initial || emptyForm);
    setError('');
    setConfirming(false);
  }, [initial, isOpen]);

  if (!isOpen) return null;

  // Step 1 — validate, then open the human-in-the-loop confirmation (STORY-155).
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError(form.rule_kind === 'slo' ? t.alerts.validationNameRequired : t.alerts.validationRequired);
      return;
    }
    if (form.rule_kind === 'metric' && (!form.metric_query.trim() || !form.threshold.trim())) {
      setError(t.alerts.validationRequired);
      return;
    }
    setError('');
    setConfirming(true);
  };

  const setKind = (kind: RuleFormData['rule_kind']) => {
    setForm((f) => (f.rule_kind === kind ? f : { ...f, rule_kind: kind, metric_query: '' }));
  };

  // Step 2 — the confirmed write actually runs.
  const doSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      await onSubmit(form);
      setConfirming(false);
      onClose();
    } catch (err: any) {
      setError(err?.message || t.alerts.saveFailed);
      setConfirming(false); // fall back to the form so the error is visible
    } finally {
      setSaving(false);
    }
  };

  // Redacted, human-readable summary of the pending change.
  const severityLabel = alabel(t, SEVERITIES.find((s) => s.value === form.severity)?.labelKey || form.severity);
  const channelLabel = form.channels.map((ch) => alabel(t, CHANNELS.find((c) => c.value === ch)?.labelKey || ch)).join(', ') || '—';
  const confirmPreview = form.rule_kind === 'slo'
    ? [
        `${t.alerts.name}: ${form.name}`,
        `${t.alerts.ruleKind}: ${t.alerts.kindSlo}`,
        `${t.alerts.service}: ${form.metric_query.trim() || t.alerts.serviceAll}`,
        `${t.alerts.severity}: ${severityLabel}`,
        `${t.alerts.channels}: ${channelLabel}`,
      ]
    : [
        `${t.alerts.name}: ${form.name}`,
        `${t.alerts.condition}: ${alabel(t, CONDITIONS.find((c) => c.value === form.condition)?.labelKey || form.condition)} ${form.threshold}`,
        `${t.alerts.severity}: ${severityLabel}`,
        `${t.alerts.channels}: ${channelLabel}`,
      ];

  const toggleChannel = (ch: string) => {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(ch) ? f.channels.filter((c) => c !== ch) : [...f.channels, ch],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button type="button" onClick={onClose} aria-label={t.common.close} title={t.common.close} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded p-2">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.ruleKind}</label>
            <div className="flex flex-wrap gap-2">
              {RULE_KINDS.map((k) => (
                <button key={k.value} type="button" onClick={() => setKind(k.value)} className={clsx('px-3 py-1 rounded-full text-xs font-medium border', form.rule_kind === k.value ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700' : 'bg-gray-50 text-gray-600 border-gray-300 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600')}>
                  {alabel(t, k.labelKey)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.name} *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldClass} placeholder={form.rule_kind === 'slo' ? t.alerts.sloNamePlaceholder : t.alerts.namePlaceholder} />
          </div>

          {form.rule_kind === 'slo' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.service}</label>
              <input value={form.metric_query} onChange={(e) => setForm({ ...form, metric_query: e.target.value })} className={fieldClass} placeholder={t.alerts.servicePlaceholder} />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t.alerts.serviceHint}</p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.metricQuery} *</label>
              <textarea value={form.metric_query} onChange={(e) => setForm({ ...form, metric_query: e.target.value })} rows={2} className={fieldClass} placeholder={t.alerts.metricQueryPlaceholder} />
            </div>
          )}

          {form.rule_kind === 'slo' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.severity}</label>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className={fieldClass}>
                {SEVERITIES.map((s) => <option key={s.value} value={s.value}>{alabel(t, s.labelKey)}</option>)}
              </select>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.condition} *</label>
                <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className={fieldClass}>
                  {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{alabel(t, c.labelKey)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.threshold} *</label>
                <input type="number" step="any" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} className={fieldClass} placeholder={t.alerts.thresholdPlaceholder} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.severity}</label>
                <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className={fieldClass}>
                  {SEVERITIES.map((s) => <option key={s.value} value={s.value}>{alabel(t, s.labelKey)}</option>)}
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.interval}</label>
            <select value={form.evaluation_interval} onChange={(e) => setForm({ ...form, evaluation_interval: Number(e.target.value) })} className={fieldClass}>
              {INTERVALS.map((i) => <option key={i.value} value={i.value}>{alabel(t, i.labelKey)}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.channels}</label>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((ch) => (
                <button key={ch.value} type="button" onClick={() => toggleChannel(ch.value)} className={clsx('px-3 py-1 rounded-full text-xs font-medium border', form.channels.includes(ch.value) ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700' : 'bg-gray-50 text-gray-600 border-gray-300 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600')}>
                  {alabel(t, ch.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {form.channels.includes('webhook') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.webhookUrl}</label>
              <input value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} className={fieldClass} placeholder={t.alerts.webhookPlaceholder} />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.description}</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={fieldClass} placeholder={t.alerts.descriptionPlaceholder} />
          </div>

          {form.rule_kind === 'metric' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t.alerts.entitySelector}</label>
              <input value={form.entity_selector} onChange={(e) => setForm({ ...form, entity_selector: e.target.value })} className={fieldClass} placeholder={t.alerts.entitySelectorPlaceholder} />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">{t.alerts.cancel}</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? t.alerts.saving : t.alerts.save}
            </button>
          </div>
        </form>
      </div>

      {/* STORY-155 — human-in-the-loop confirmation before the write runs */}
      <ConfirmDialog
        isOpen={confirming}
        title={t.alerts.confirmSaveTitle}
        message={t.alerts.confirmSaveMessage}
        preview={confirmPreview}
        confirmLabel={t.alerts.confirmSaveButton}
        cancelLabel={t.alerts.back}
        loading={saving}
        onConfirm={doSubmit}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Events Panel
// ---------------------------------------------------------------------------

const EventsPanel: React.FC<{ events: AlertEventResponse[]; loading: boolean }> = ({ events, loading }) => {
  const { t, language } = useTranslation();
  if (loading) {
    return <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-4"><Loader2 className="h-4 w-4 animate-spin" /> {t.common.loading}</div>;
  }
  if (events.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 py-4">{t.alerts.noEvents}</p>;
  }
  return (
    <div className="space-y-2">
      {events.map((ev) => {
        const text = formatEventMessage(ev, {
          eventFiringMsg: alabel(t, 'eventFiringMsg'),
          eventResolvedMsg: alabel(t, 'eventResolvedMsg'),
        });
        return (
        <div key={ev.id} className="flex items-start gap-3 p-3 rounded-md bg-gray-50 dark:bg-gray-700/50">
          <div className="pt-0.5">{ev.status === 'firing' ? <Flame className="h-4 w-4 text-red-500" /> : <CheckCircle className="h-4 w-4 text-green-500" />}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              {statusBadge(t, ev.status)}
              {ev.rule_name && <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{ev.rule_name}</span>}
            </div>
            {text && <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words">{text}</p>}
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {formatDateTime(ev.fired_at, language)}
              {ev.resolved_at && <> &mdash; {t.alerts.resolvedAt} {formatDateTime(ev.resolved_at, language)}</>}
            </p>
          </div>
          {ev.current_value != null && (
            <span className="text-sm font-mono text-gray-700 dark:text-gray-300 whitespace-nowrap">{storedNumber(ev.current_value)}</span>
          )}
        </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const AlertSettings: React.FC = () => {
  const { t, language } = useTranslation();
  const {
    rules, events, summary, loading, eventsLoading, error,
    fetchRules, createRule, updateRule, deleteRule, fetchEvents, fetchSummary, clearError,
  } = useAlertStore();

  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRuleResponse | null>(null);
  const [deletingRule, setDeletingRule] = useState<AlertRuleResponse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedRule, setExpandedRule] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'rules' | 'events'>('rules');
  const [eventFilter, setEventFilter] = useState<string>('');

  useEffect(() => {
    fetchRules();
    fetchSummary();
  }, []);

  useEffect(() => {
    if (activeTab === 'events') {
      fetchEvents(eventFilter ? { status: eventFilter } : undefined);
    }
  }, [activeTab, eventFilter]);

  const handleCreate = async (form: RuleFormData) => {
    await createRule(ruleFormPayload(form));
    fetchSummary();
  };

  const handleEdit = async (form: RuleFormData) => {
    if (!editingRule) return;
    await updateRule(editingRule.id, ruleFormPayload(form));
    setEditingRule(null);
    fetchSummary();
  };

  const handleToggle = async (rule: AlertRuleResponse) => {
    await updateRule(rule.id, { is_enabled: !rule.is_enabled });
    fetchSummary();
  };

  // Delete goes through the same human-in-the-loop confirm dialog (STORY-155).
  const confirmDelete = async () => {
    if (!deletingRule) return;
    setDeleting(true);
    try {
      await deleteRule(deletingRule.id);
      setDeletingRule(null);
      fetchSummary();
    } finally {
      setDeleting(false);
    }
  };

  const handleExpandRule = (ruleId: number) => {
    if (expandedRule === ruleId) {
      setExpandedRule(null);
    } else {
      setExpandedRule(ruleId);
      fetchEvents({ rule_id: ruleId });
    }
  };

  const ruleToForm = (r: AlertRuleResponse): RuleFormData => ({
    name: r.name,
    rule_kind: r.rule_kind === 'slo' ? 'slo' : 'metric',
    metric_query: r.metric_query || '',
    condition: r.condition || 'gt',
    threshold: r.threshold == null ? '' : String(r.threshold),
    severity: r.severity,
    channels: r.notification_channels?.channels || ['in_app'],
    webhook_url: r.notification_channels?.webhook_url || '',
    evaluation_interval: r.evaluation_interval,
    description: r.description || '',
    entity_selector: r.entity_selector || '',
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {t.alerts.title}
          </h2>
          {summary && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t.alerts.enabledOfTotal
                .replace('{enabled}', String(summary.enabled_rules))
                .replace('{total}', formatCount(language, summary.total_rules, t.alerts.totalRules))}
              {summary.firing > 0 && (
                <span className="text-red-600 dark:text-red-400 ml-2">
                  {formatCount(language, summary.firing, t.alerts.firingCount)}
                </span>
              )}
            </p>
          )}
        </div>
        <button onClick={() => { setEditingRule(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
          <Plus className="h-4 w-4" /> {t.alerts.newRule}
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          <AlertTriangle className="h-4 w-4" />
          {error}
          <button type="button" onClick={clearError} aria-label={t.common.dismiss} title={t.common.dismiss} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
        <button onClick={() => setActiveTab('rules')} className={clsx('px-4 py-2 text-sm font-medium border-b-2 -mb-px', activeTab === 'rules' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400')}>
          {t.alerts.rules} ({rules.length})
        </button>
        <button onClick={() => setActiveTab('events')} className={clsx('px-4 py-2 text-sm font-medium border-b-2 -mb-px', activeTab === 'events' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400')}>
          <span className="flex items-center gap-1"><History className="h-4 w-4" /> {t.alerts.events}</span>
        </button>
      </div>

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <>
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-8 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> {t.common.loading}</div>
          ) : rules.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-medium mb-1">{t.alerts.noRules}</p>
              <p className="text-sm">{t.alerts.noRulesHint}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div key={rule.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800">
                    <button type="button" onClick={() => handleExpandRule(rule.id)} aria-expanded={expandedRule === rule.id} aria-label={expandedRule === rule.id ? t.alerts.hideEvents : t.alerts.showEvents} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                      {expandedRule === rule.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={clsx('font-medium text-sm', rule.is_enabled ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500')}>{rule.name}</span>
                        {severityBadge(t, rule.severity)}
                        {!rule.is_enabled && <span className="text-xs text-gray-400 dark:text-gray-500">({t.alerts.disabled.toLowerCase()})</span>}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {rule.rule_kind === 'slo'
                          ? (rule.metric_query?.trim() ? `${t.alerts.service}: ${rule.metric_query}` : t.alerts.serviceAll)
                          : rule.metric_query}
                      </p>
                      {rule.last_skip_reason && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">{alabel(t, skipBannerKey(rule))}</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {rule.rule_kind === 'slo' ? t.alerts.kindSlo : `${rule.condition} ${rule.threshold}`}
                    </span>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => handleToggle(rule)} title={rule.is_enabled ? t.alerts.disableRule : t.alerts.enableRule} aria-label={rule.is_enabled ? t.alerts.disableRule : t.alerts.enableRule} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                        {rule.is_enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </button>
                      <button onClick={() => { setEditingRule(rule); setShowForm(true); }} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 text-xs">{t.common.edit}</button>
                      <button type="button" onClick={() => setDeletingRule(rule)} title={t.alerts.deleteRule} aria-label={t.alerts.deleteRule} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {expandedRule === rule.id && (
                    <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600 dark:text-gray-400 mb-3">
                        <div>{t.alerts.intervalEvery.replace('{seconds}', String(rule.evaluation_interval))}</div>
                        <div>{t.alerts.lastEvaluated}: {formatDateTime(rule.last_evaluated_at, language)}</div>
                        {rule.description && <div className="col-span-2">{t.alerts.description}: {rule.description}</div>}
                        {rule.entity_selector && <div className="col-span-2">{t.alerts.entitySelector}: {rule.entity_selector}</div>}
                      </div>
                      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1"><History className="h-3 w-3" /> {t.alerts.recentEvents}</h4>
                      <EventsPanel events={events.filter((e) => e.rule_id === rule.id)} loading={eventsLoading} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
        <>
          <div className="flex gap-2 mb-4">
            {['', 'firing', 'resolved'].map((f) => (
              <button key={f} onClick={() => setEventFilter(f)} className={clsx('px-3 py-1 rounded-full text-xs font-medium', eventFilter === f ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400')}>
                {f === 'firing' ? t.alerts.firing : f === 'resolved' ? t.alerts.resolved : t.common.all}
              </button>
            ))}
          </div>
          <EventsPanel events={events} loading={eventsLoading} />
        </>
      )}

      {/* Create / Edit Modal */}
      <FormModal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingRule(null); }}
        onSubmit={editingRule ? handleEdit : handleCreate}
        initial={editingRule ? ruleToForm(editingRule) : undefined}
        title={editingRule ? t.alerts.editRule : t.alerts.newRule}
      />

      {/* Delete confirmation (STORY-155 — human-in-the-loop) */}
      <ConfirmDialog
        isOpen={!!deletingRule}
        danger
        title={t.alerts.confirmDeleteTitle}
        message={t.alerts.deleteConfirm}
        preview={deletingRule ? [`${t.alerts.name}: ${deletingRule.name}`] : []}
        confirmLabel={t.alerts.confirmDeleteButton}
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeletingRule(null)}
      />
    </div>
  );
};
